import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser, canClinical } from "../api";
import { navUrl } from "../lib/navUrl";
import { Avatar } from "../components/Posts";
import { PatientLink } from "../components/PatientLink";
import { formatProName } from "../lib/proName";
import { PdfViewer } from "../components/PdfViewer";
import { toast } from "sonner";

const ts = (iso) => (iso ? new Date(iso).getTime() : 0);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const eur = (c) => (typeof c === "number" && Number.isFinite(c) && c > 0 ? `€ ${(c / 100).toFixed(2)}` : "—");

function ConsultCard({ b, t, u, onCancel, onRedact }) {
  const { i18n } = useTranslation();
  const start = ts(b.scheduled_at);
  const now = Date.now();
  // Finestra temporale esistente: sala apre 10 min prima e resta fino a fine consulto (30 min)
  const roomOpen = ["confirmed", "in_progress"].includes(b.status) && now >= start - 10 * 60 * 1000 && now <= start + 30 * 60 * 1000;
  const payKey = b.status === "cancelled_refunded" ? "pay_refunded"
    : b.payment_status === "paid" ? "pay_paid"
    : b.status === "pending" ? "pay_pending" : "pay_none";
  const isPro = u?.role === "professional";
  const otherId = isPro ? b.user_id : b.professional_id;
  const otherName = isPro ? b.patient_name : formatProName({ title: b.professional_title, name: b.professional_name }, i18n.language);
  return (
    <div className="yp-card p-4" data-testid="booking-consult-card">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <span className="yp-badge yp-badge-approved" data-testid="booking-type">{t("bookings.type_consult")}</span>
          <div className="font-medium mt-2" data-testid="consult-datetime">{fmt(b.scheduled_at)}</div>
          {otherName && !isPro && (
            <Link to={`/professionals/${b.professional_id}`} className="flex items-center gap-2 mt-1 rounded-lg -m-1 p-1 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-fit" data-testid="consult-pro-link">
              <Avatar url={b.professional_avatar_url} label={otherName} cls="w-9 h-9 text-sm" />
              <span className="text-sm text-blue-600 hover:underline">
                {otherName}{b.professional_specialization ? ` · ${b.professional_specialization}` : ""}
              </span>
            </Link>)}
          {otherName && isPro && (
            <div className="mt-1">
              <PatientLink id={b.user_id} name={otherName} avatarUrl={b.patient_avatar_url} testid="consult-patient-link" />
            </div>)}
          <div className="text-xs text-slate-500 mt-1">
            {t("bookings.duration")}: {t("bookings.duration_value")} · {t("bookings.amount")}: <span data-testid="consult-amount">{eur(b.price_cents)}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {t("bookings.status")}: <span data-testid="consult-status">{t(`bookings.st_${b.status}`)}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          {roomOpen && <Link to={`/video/${b.id}`} className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" data-testid="enter-room-btn">{t("bookings.enter_room")}</Link>}
          <Link to={`/chat/${otherId}`} className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" data-testid="consult-chat-link">{t("bookings.open_chat")}</Link>
          {canClinical(u) && onRedact && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => onRedact({ episode: { patient_id: b.user_id, episode_type: "videoconsulto", vc_id: b.id, date: b.scheduled_at } })} data-testid="consult-redact-btn">Redigi documento clinico</button>}
          {b.status === "confirmed" && u?.role === "user" && <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs" onClick={() => onCancel(b.id)} data-testid="cancel-booking-btn">{t("bookings.cancel_booking")}</button>}
        </div>
      </div>
    </div>
  );
}

function EventCard({ r, t, onTicket, onViewTicket }) {
  const stKey = r.status === "confirmed" ? "reg_confirmed" : r.status === "waitlist" ? "reg_waitlist" : "reg_cancelled";
  return (
    <div className="yp-card p-4" data-testid="booking-event-card">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="yp-badge yp-badge-approved" data-testid="booking-type">{t("bookings.type_event")}</span>
            <span className={`yp-badge ${r.status === "cancelled" ? "yp-badge-rejected" : "yp-badge-approved"}`} data-testid="event-reg-status">{t(`bookings.${stKey}`)}</span>
            {r.checked_in && <span className="yp-badge yp-badge-approved" data-testid="checked-in-badge">{t("bookings.checked_in")}</span>}
          </div>
          <Link to={`/events/${r.event_id}`} className="font-medium mt-2 block hover:underline" data-testid="event-title-link">{r.event_title}</Link>
          {r.organizer_name && (
            <Link to={r.organizer_role === "organization" ? `/organizations/${r.organizer_id}` : `/professionals/${r.organizer_id}`}
              className="flex items-center gap-2 mt-1 rounded-lg -m-1 p-1 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-fit" data-testid="event-org-link">
              <Avatar url={r.organizer_avatar_url} label={r.organizer_name} cls="w-9 h-9 text-sm" />
              <span className="text-sm text-blue-600 hover:underline">{r.organizer_name}</span>
            </Link>)}
          <div className="text-xs text-slate-500 mt-1" data-testid="event-when-where">{fmt(r.start_at)} · 📍 {r.address}</div>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          {r.ticket_available && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => onTicket(r.id)} data-testid="download-ticket-btn">{t("bookings.download_ticket")}</button>}
          {r.ticket_available && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => onViewTicket(r.id)} data-testid="view-ticket-btn">{t("bookings.view_ticket")}</button>}
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ b, t, u, i18nLang, onComplete, onRedact }) {
  const eurF = (c) => ((c || 0) / 100).toLocaleString(i18nLang, { style: "currency", currency: "EUR" });
  const role = u?.role;
  const nav = navUrl(b.nav_address || b.location_label);
  const canComplete = b.status === "confirmed" && ((b.professional_mode === "pro" && role === "professional" && u?.id === b.professional_id) || (b.professional_mode !== "pro" && role === "organization"));
  return (
    <div className="yp-card p-4" data-testid="booking-service-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="yp-badge yp-badge-paid" data-testid="booking-type-service">{t("services.service")}</span>
        <span className="yp-badge yp-badge-approved" data-testid="service-status">{t(`bookings.st_${b.status}`)}</span>
      </div>
      <div className="font-medium mt-2" data-testid="service-name">{b.service_name}</div>
      {b.org_name && (
        <div className="flex items-center gap-2 mt-1" data-testid="service-facility">
          {b.org_id ? (
            <Link to={`/organizations/${b.org_id}`} className="flex items-center gap-2 rounded-lg -m-1 p-1 hover:bg-slate-50 w-fit" data-testid="service-facility-link">
              <Avatar url={b.org_avatar_url} label={b.org_name} cls="w-7 h-7 text-xs" />
              <span className="text-xs text-blue-600 hover:underline">{t("services.facility")}: {b.org_name}</span>
            </Link>
          ) : (<><Avatar url={b.org_avatar_url} label={b.org_name} cls="w-7 h-7 text-xs" /><span className="text-xs text-slate-600">{t("services.facility")}: {b.org_name}</span></>)}
        </div>)}
      {b.professional_name && (
        <div className="flex items-center gap-2 mt-1" data-testid="service-professional">
          {b.professional_id ? (
            <Link to={`/professionals/${b.professional_id}`} className="flex items-center gap-2 rounded-lg -m-1 p-1 hover:bg-slate-50 w-fit" data-testid="service-pro-link">
              {b.professional_avatar_url && <Avatar url={b.professional_avatar_url} label={b.professional_name} cls="w-7 h-7 text-xs" />}
              <span className="text-xs text-blue-600 hover:underline">{t("services.professional_optional")}: {formatProName({ title: b.professional_title, name: b.professional_name }, i18nLang)}</span>
            </Link>
          ) : (<>{b.professional_avatar_url && <Avatar url={b.professional_avatar_url} label={b.professional_name} cls="w-7 h-7 text-xs" />}<span className="text-xs text-slate-600">{t("services.professional_optional")}: {formatProName({ title: b.professional_title, name: b.professional_name }, i18nLang)}</span></>)}
        </div>)}
      {(role === "professional" || role === "organization") && b.patient_name && (
        <div className="mt-1"><PatientLink id={b.user_id} name={b.patient_name} avatarUrl={b.patient_avatar_url} cls="w-8 h-8 text-xs" testid="service-patient-link" /></div>)}
      <div className="text-xs text-slate-500 mt-1" data-testid="service-datetime">{fmt(b.scheduled_at)} · {b.duration_minutes} min</div>
      <div className="text-xs text-slate-500 mt-1" data-testid="service-place">📍 {b.location_label}</div>
      {role !== "professional" && <div className="text-xs text-slate-500 mt-1">{t("bookings.amount")}: <span data-testid="service-amount">{eurF(b.price_cents)}</span></div>}
      {nav && <a href={nav} target="_blank" rel="noopener noreferrer" className="yp-btn yp-btn-outline !py-1 !px-3 text-xs mt-2 inline-block" data-testid="service-open-maps">📍 {t("services.open_maps")}</a>}
      {canComplete && onComplete && <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs mt-2 ml-2 inline-block" data-testid="service-complete-btn" onClick={() => onComplete(b)}>{t("services.mark_completed")}</button>}
      {canClinical(u) && onRedact && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs mt-2 ml-2 inline-block" data-testid="service-redact-btn" onClick={() => onRedact({ episode: { patient_id: b.user_id, episode_type: "servizio", service_id: b.id, date: b.scheduled_at } })}>Redigi documento clinico</button>}
    </div>);
}

function OrgEventCard({ r, t, i18nLang }) {
  const eurF = (c) => ((c || 0) / 100).toLocaleString(i18nLang, { style: "currency", currency: "EUR" });
  const stKey = r.status === "confirmed" ? "reg_confirmed" : r.status === "waitlist" ? "reg_waitlist" : r.status === "offered" ? "reg_waitlist" : "reg_cancelled";
  return (
    <div className="yp-card p-4" data-testid="org-event-reg-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="yp-badge yp-badge-approved" data-testid="booking-type">{t("bookings.type_event")}</span>
        <span className={`yp-badge ${r.is_paid ? "yp-badge-paid" : "yp-badge-approved"}`} data-testid="org-event-paid-badge">{r.is_paid ? t("bookings.paid_label") : t("bookings.free_label")}</span>
        <span className="yp-badge yp-badge-approved" data-testid="org-event-reg-status">{t(`bookings.${stKey}`)}</span>
        {r.checked_in && <span className="yp-badge yp-badge-approved" data-testid="org-event-checked-in">{t("bookings.checked_in")}</span>}
      </div>
      <Link to={`/events/${r.event_id}`} className="font-medium mt-2 block hover:underline" data-testid="org-event-title">{r.event_title}</Link>
      <div className="flex items-center gap-2 mt-1">
        <PatientLink id={r.participant_id} name={r.participant_name} avatarUrl={r.participant_avatar_url} cls="w-8 h-8 text-xs" testid="org-event-participant-link" />
      </div>
      <div className="text-xs text-slate-500 mt-1" data-testid="org-event-when">{fmt(r.start_at)}</div>
      {r.is_paid && <div className="text-xs text-slate-500 mt-1">{t("bookings.amount_paid")}: <span data-testid="org-event-amount">{eurF(r.amount_paid_cents)}</span></div>}
    </div>);
}

function InPersonCard({ b, t, u, onComplete, onRedact }) {
  const { i18n } = useTranslation();
  const place = b.location_label;
  const nav = navUrl(b.nav_address || (b.location_kind === "free" ? b.location_label : ""));
  const role = u?.role;
  const proName = formatProName({ title: b.professional_title, name: b.professional_name }, i18n.language);
  return (
    <div className="yp-card p-4" data-testid="booking-inperson-card">
      <span className="yp-badge yp-badge-approved" data-testid="booking-type-inperson">{t("inperson.type_inperson")}</span>
      <div className="font-medium mt-2" data-testid="inperson-datetime">{fmt(b.scheduled_at)}</div>
      {(role === "user" || role === "organization") && b.professional_name && (
        <div className="flex items-center gap-2 mt-1">
          {b.professional_id ? (
            <Link to={`/professionals/${b.professional_id}`} className="flex items-center gap-2 rounded-lg -m-1 p-1 hover:bg-slate-50 w-fit" data-testid="inperson-pro-link">
              <Avatar url={b.professional_avatar_url} label={proName} cls="w-8 h-8 text-xs" />
              <span className="text-sm text-blue-600 hover:underline" data-testid="inperson-professional">{proName}</span>
            </Link>
          ) : (<><Avatar url={b.professional_avatar_url} label={proName} cls="w-8 h-8 text-xs" /><span className="text-sm text-slate-600" data-testid="inperson-professional">{proName}</span></>)}
        </div>)}
      {(role === "professional" || role === "organization") && b.patient_name && (
        <div className="mt-1">
          <PatientLink id={b.user_id} name={b.patient_name} avatarUrl={b.patient_avatar_url} cls="w-8 h-8 text-xs" testid="inperson-patient-link" />
        </div>)}
      <div className="text-xs text-slate-500 mt-1" data-testid="inperson-place">📍 {place}</div>
      <div className="text-xs text-slate-500 mt-1">
        {t("bookings.duration")}: {b.duration_minutes} min · {t("bookings.amount")}: <span data-testid="inperson-amount">{eur(b.price_cents)}</span>
      </div>
      <div className="text-xs text-slate-500 mt-1">{t("bookings.status")}: <span data-testid="inperson-status">{t(`bookings.st_${b.status}`)}</span></div>
      {nav && <a href={nav} target="_blank" rel="noopener noreferrer" className="yp-btn yp-btn-outline !py-1 !px-3 text-xs mt-2 inline-block" data-testid="inperson-open-maps">📍 {t("inperson.open_maps")}</a>}
      {role === "professional" && b.status === "confirmed" && onComplete && (
        <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs mt-2 ml-2 inline-block" data-testid="inperson-complete-btn" onClick={() => onComplete(b)}>{t("inperson.mark_completed")}</button>)}
      {canClinical(u) && onRedact && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs mt-2 ml-2 inline-block" data-testid="inperson-redact-btn" onClick={() => onRedact({ episode: { patient_id: b.user_id, episode_type: "visita", booking_id: b.id, date: b.scheduled_at } })}>Redigi documento clinico</button>}
    </div>);
}

export default function Bookings() {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [tab, setTab] = useState("all");
  const [bs, setBs] = useState([]);
  const [regs, setRegs] = useState([]);
  const [ips, setIps] = useState([]);
  const [orgRegs, setOrgRegs] = useState([]); // iscrizioni eventi della struttura
  const [sbs, setSbs] = useState([]); // prenotazioni servizi (tutti i ruoli)
  const [loaded, setLoaded] = useState(false);
  const [ticketView, setTicketView] = useState(null); // {rid, url} blob del PDF in viewer modale
  const navigate = useNavigate();
  const goRedact = (ctx) => {
    const e = ctx.episode || {};
    const p = new URLSearchParams({ ep: e.episode_type || "documento" });
    if (e.vc_id) p.set("vc", e.vc_id);
    if (e.booking_id) p.set("booking", e.booking_id);
    if (e.service_id) p.set("service", e.service_id);
    if (e.date) p.set("date", e.date);
    navigate(`/clinica/${e.patient_id}?${p.toString()}`);
  };
  const load = () => {
    const jobs = [api.get("/in-person-bookings").then(({ data }) => setIps(data)).catch(() => {}),
      api.get("/service-bookings").then(({ data }) => setSbs(data)).catch(() => {})];
    if (u?.role !== "organization") jobs.push(api.get("/me/bookings").then(({ data }) => setBs(data)));
    if (u?.role === "user") jobs.push(api.get("/me/event-registrations").then(({ data }) => setRegs(data)));
    if (u?.role === "organization") jobs.push(api.get("/org/event-registrations").then(({ data }) => setOrgRegs(data)).catch(() => {}));
    Promise.all(jobs).finally(() => setLoaded(true));
  };
  useEffect(() => { load(); }, []);
  // Al ritorno dal checkout (o quando la scheda riottiene il focus) ricarica dati freschi dall'API: nessuno stato in cache.
  useEffect(() => {
    const onShow = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onShow);
    document.addEventListener("visibilitychange", onShow);
    return () => { window.removeEventListener("focus", onShow); document.removeEventListener("visibilitychange", onShow); };
  }, []);
  const cancel = async (id) => { try { const { data } = await api.post(`/bookings/${id}/cancel`); toast.success(data.refund ? t("bookings.st_cancelled_refunded") : t("bookings.st_cancelled_no_refund")); load(); } catch (e) { toast.error(e.response?.data?.detail); } };
  const ticket = async (rid) => {
    try {
      const { data } = await api.get(`/me/event-registrations/${rid}/ticket`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `ticket-${rid}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error(t("bookings.ticket_err")); }
  };
  const viewTicket = async (rid) => {
    // Visualizzatore pdf.js interno con blob autenticato: niente popup, funziona su Safari iOS/PWA e Android
    try {
      const { data } = await api.get(`/me/event-registrations/${rid}/ticket`, { responseType: "blob" });
      setTicketView({ rid, blob: new Blob([data], { type: "application/pdf" }) });
    } catch { toast.error(t("bookings.ticket_err")); }
  };
  const closeTicketView = useCallback(() => setTicketView(null), []);
  const completeInPerson = async (b) => {
    try { await api.post(`/in-person-bookings/${b.id}/complete`); toast.success(t("inperson.mark_completed")); load(); }
    catch { toast.error(t("common.error")); }
  };
  const completeService = async (b) => {
    try { await api.post(`/service-bookings/${b.id}/complete`); toast.success(t("services.mark_completed")); load(); }
    catch { toast.error(t("common.error")); }
  };
  if (u?.role === "organization") {
    const orgMixed = [
      ...ips.map((b) => ({ kind: "i", when: ts(b.created_at), data: b })),
      ...orgRegs.map((r) => ({ kind: "e", when: ts(r.registered_at), data: r })),
      ...sbs.map((b) => ({ kind: "s", when: ts(b.created_at), data: b })),
    ].sort((a, b) => (b.when || 0) - (a.when || 0));
    return (
    <Layout><div className="yp-container py-8" data-testid="bookings-page">
      <h1 className="text-3xl font-bold mb-6">{t("nav.bookings")}</h1>
      <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="bookings-tabs" role="tablist">
        {[["all", t("bookings.tab_all")], ["events", t("bookings.tab_events")], ["services", t("bookings.tab_services")]].map(([k, lbl]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`yp-btn !py-1.5 !px-4 text-sm shrink-0 ${tab === k ? "yp-btn-primary" : "yp-btn-outline"}`}
            data-testid={`tab-${k}`}>{lbl}</button>))}
      </div>
      {!loaded && <div className="text-slate-400">{t("common.loading")}</div>}
      {loaded && tab === "all" && (
        <div className="space-y-3" data-testid="tab-all-list">
          {orgMixed.length === 0 && <div className="text-slate-400" data-testid="empty-all">{t("bookings.empty_all")}</div>}
          {orgMixed.map((x) => x.kind === "i"
            ? <InPersonCard key={x.data.id} b={x.data} t={t} u={u} onRedact={goRedact} />
            : x.kind === "s"
            ? <ServiceCard key={x.data.id} b={x.data} t={t} u={u} i18nLang={i18n.language} onComplete={completeService} onRedact={goRedact} />
            : <OrgEventCard key={x.data.id} r={x.data} t={t} i18nLang={i18n.language} />)}
        </div>)}
      {loaded && tab === "events" && (
        <div className="space-y-3" data-testid="tab-events-list">
          {orgRegs.length === 0 && <div className="text-slate-400" data-testid="empty-events">{t("bookings.empty_events")}</div>}
          {orgRegs.map((r) => <OrgEventCard key={r.id} r={r} t={t} i18nLang={i18n.language} />)}
        </div>)}
      {loaded && tab === "services" && (
        <div className="space-y-3" data-testid="tab-services-list">
          {sbs.length === 0 && <div className="text-slate-400" data-testid="empty-services">{t("services.empty")}</div>}
          {sbs.map((b) => <ServiceCard key={b.id} b={b} t={t} u={u} i18nLang={i18n.language} onComplete={completeService} onRedact={goRedact} />)}
        </div>)}
    </div></Layout>);
  }
  const isUser = u?.role === "user";
  const isPro = u?.role === "professional";
  const ipList = ips.map((b) => <InPersonCard key={b.id} b={b} t={t} u={u} onComplete={completeInPerson} onRedact={goRedact} />);
  // Ordine per momento EFFETTIVO di prenotazione/iscrizione (più recente prima), NON per data programmata.
  // Consulti: created_at (per "Prenota ora" = istante pagamento). Eventi: registered_at (event_regs.at).
  // Record privi di timestamp reale finiscono in fondo (mai in cima), senza date inventate.
  const cmpDesc = (x, y) => { if (!x && !y) return 0; if (!x) return 1; if (!y) return -1; return y - x; };
  // Ordina consulti (tutte le schede + lato professionista) ed eventi per timestamp di prenotazione
  const consults = bs.filter((b) => b.status !== "failed").slice().sort((a, b) => cmpDesc(ts(a.created_at), ts(b.created_at)));
  const sortedRegs = regs.slice().sort((a, b) => cmpDesc(ts(a.registered_at), ts(b.registered_at)));
  // "Tutto" include Videoconsulti + Visite in presenza + Eventi, ordinati per momento di acquisto/prenotazione (created_at/registered_at), più recente in cima.
  const mixed = [
    ...consults.map((b) => ({ kind: "c", when: ts(b.created_at), data: b })),
    ...sortedRegs.map((r) => ({ kind: "e", when: ts(r.registered_at), data: r })),
    ...ips.map((b) => ({ kind: "i", when: ts(b.created_at), data: b })),
    ...sbs.map((b) => ({ kind: "s", when: ts(b.created_at), data: b })),
  ];
  const allItems = [...mixed].sort((a, b) => cmpDesc(a.when, b.when));
  const consultList = (
    <div className="space-y-3" data-testid="tab-consults-list">
      {consults.length === 0 && <div className="text-slate-400" data-testid="empty-consults">{t("bookings.empty_consults")}</div>}
      {consults.map((b) => <ConsultCard key={b.id} b={b} t={t} u={u} onCancel={cancel} onRedact={goRedact} />)}
    </div>);
  return (
    <Layout><div className="yp-container py-8" data-testid="bookings-page">
      <h1 className="text-3xl font-bold mb-6">{t("nav.bookings")}</h1>
      {isUser && (
        <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="bookings-tabs" role="tablist">
          {[["all", t("bookings.tab_all")], ["consults", t("bookings.tab_consults")], ["inperson", t("inperson.tab_inperson")], ["services", t("services.tab_services")], ["events", t("bookings.tab_events")]].map(([k, lbl]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              className={`yp-btn !py-1.5 !px-4 text-sm shrink-0 ${tab === k ? "yp-btn-primary" : "yp-btn-outline"}`}
              data-testid={`tab-${k}`}>{lbl}</button>))}
        </div>)}
      {isPro && (
        <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="bookings-tabs" role="tablist">
          {[["all", t("bookings.tab_all")], ["consults", t("bookings.tab_consults")], ["inperson", t("inperson.tab_inperson")], ["services", t("services.tab_services")]].map(([k, lbl]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              className={`yp-btn !py-1.5 !px-4 text-sm shrink-0 ${tab === k ? "yp-btn-primary" : "yp-btn-outline"}`}
              data-testid={`tab-${k}`}>{lbl}</button>))}
        </div>)}
      {!loaded && <div className="text-slate-400">{t("common.loading")}</div>}
      {loaded && (isUser || isPro) && tab === "all" && (
        <div className="space-y-3" data-testid="tab-all-list">
          {allItems.length === 0 && <div className="text-slate-400" data-testid="empty-all">{t("bookings.empty_all")}</div>}
          {allItems.map((i) => i.kind === "c"
            ? <ConsultCard key={i.data.id} b={i.data} t={t} u={u} onCancel={cancel} onRedact={goRedact} />
            : i.kind === "i"
            ? <InPersonCard key={i.data.id} b={i.data} t={t} u={u} onComplete={completeInPerson} onRedact={goRedact} />
            : i.kind === "s"
            ? <ServiceCard key={i.data.id} b={i.data} t={t} u={u} i18nLang={i18n.language} onComplete={completeService} onRedact={goRedact} />
            : <EventCard key={i.data.id} r={i.data} t={t} onTicket={ticket} onViewTicket={viewTicket} />)}
        </div>)}
      {loaded && (isUser || isPro) && tab === "consults" && consultList}
      {loaded && (isUser || isPro) && tab === "services" && (
        <div className="space-y-3" data-testid="tab-services-list">
          {sbs.length === 0 && <div className="text-slate-400" data-testid="empty-services">{t("services.empty")}</div>}
          {sbs.map((b) => <ServiceCard key={b.id} b={b} t={t} u={u} i18nLang={i18n.language} onComplete={completeService} onRedact={goRedact} />)}
        </div>)}
      {loaded && (isUser || isPro) && tab === "inperson" && (
        <div className="space-y-3" data-testid="tab-inperson-list">
          {ips.length === 0 && <div className="text-slate-400" data-testid="empty-inperson">{t("inperson.empty")}</div>}
          {ipList}
        </div>)}
      {loaded && isUser && tab === "events" && (
        <div className="space-y-3" data-testid="tab-events-list">
          {sortedRegs.length === 0 && <div className="text-slate-400" data-testid="empty-events">{t("bookings.empty_events")}</div>}
          {sortedRegs.map((r) => <EventCard key={r.id} r={r} t={t} onTicket={ticket} onViewTicket={viewTicket} />)}
        </div>)}
      {ticketView && <PdfViewer blob={ticketView.blob} filename={`ticket-${ticketView.rid}.pdf`} onClose={closeTicketView} testid="ticket-viewer" />}
    </div></Layout>
  );
}
